import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import {
  Button,
  Chip,
  CurrenciesUSD,
  Icon,
  SegmentedControl,
  SegmentedControlVariant,
  Table,
  TableColumn,
  TableVariant,
  formatNumber,
} from '@column/column-ui-kit';

import {
  buildBreakdownText,
  getBatchStatusDisplay,
  pickBatchAmounts,
} from '~/app/pages/Transfers/AchBatches/_components/recordHelpers';
import { RecordsHoverText } from '~/app/pages/Transfers/AchBatches/_components/RecordsHoverText';
import { ROUTE } from '~/app/routes';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { AchBatchRepository } from '~/repositories/Transfer/AchBatchRepository';
import { TransferBatchRepository } from '~/repositories/Transfer/TransferBatchRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { ACHTransferBatch, TransferBatch } from '~/typings/API';

type Tab = 'check' | 'ach';

const TabBar = styled.div`
  padding: 0 var(--page-margin) 16px;
`;

type AmountSign = 'positive' | 'negative' | 'zero';

const AmountRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  width: 100%;
  height: 40px;
  padding: 8px 16px;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 24px;
  white-space: nowrap;
  font-weight: 500;
`;

const AmountValue = styled.span<{ $sign: AmountSign }>`
  color: ${({ theme, $sign }) =>
    $sign === 'positive'
      ? theme.success.background
      : $sign === 'zero'
        ? theme.secondary.blendToBackground(800)
        : theme.foreground};

  ${({ $sign }) =>
    $sign === 'negative' &&
    css`
      position: relative;

      &:before {
        content: '-';
        position: absolute;
        right: 100%;
        display: block;
        top: 0;
        font: inherit;
        line-height: inherit;
      }
    `}
`;

const AmountNote = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  min-width: 0;
`;

const signOf = (value: number): AmountSign => (value > 0 ? 'positive' : value < 0 ? 'negative' : 'zero');

const RecordsText = styled.span`
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

const BatchAmountCell: React.FC<{ batch: ACHTransferBatch }> = ({ batch }) => {
  const picked = pickBatchAmounts(batch);
  const value = picked ? picked.credit - picked.debit : null;
  return (
    <AmountRow>
      {value === null ? (
        <AmountValue $sign="zero">-</AmountValue>
      ) : (
        <AmountValue $sign={signOf(value)}>{formatNumber(Math.abs(value), 'USD')}</AmountValue>
      )}
      {batch.description ? <AmountNote>{batch.description}</AmountNote> : null}
    </AmountRow>
  );
};

const checkColumns: TableColumn<TransferBatch>[] = [
  {
    header: 'Amount / Description',
    accessor: 'totalAmount',
    width: { min: 150, max: '4fr' },
    cell: ({ row, value }) => (
      <Table.AmountCell
        currencyList={CurrenciesUSD}
        fixedCurrencyCode={row.totalCurrency}
        value={value}
        note={row.description ?? undefined}
        isBold
        isColorized
      />
    ),
  },
  {
    header: 'Transfers',
    accessor: 'transferCount',
    width: { min: 100, max: '1fr' },
    cell: ({ value }) => <Table.TextCell value={value?.toString() ?? ''} placeholder="Transfer Count" />,
  },
  {
    header: 'Created',
    accessor: 'createdAt',
    width: { fixed: 80 },
    cell: Table.TimeCell,
  },
];

const achColumns: TableColumn<ACHTransferBatch>[] = [
  {
    header: 'Amount / Description',
    accessor: 'totalCreditAmount',
    width: { min: 150, max: '4fr' },
    cell: ({ row }) => <BatchAmountCell batch={row} />,
  },
  {
    header: 'Records',
    accessor: 'totalCount',
    width: { min: 80, max: '1fr' },
    cell: ({ row }) => (
      <Table.Cell>
        <RecordsText>
          <RecordsHoverText totalText={String(row.totalCount)} breakdownText={buildBreakdownText(row)} />
        </RecordsText>
      </Table.Cell>
    ),
  },
  {
    header: 'Status',
    accessor: 'status',
    width: { min: 130, max: '1fr' },
    cell: ({ row }) => {
      const meta = getBatchStatusDisplay(row);
      return (
        <Table.Cell>
          <Chip variant="subtle" type={meta.type} icon={meta.icon}>
            {meta.label}
          </Chip>
        </Table.Cell>
      );
    },
  },
  {
    header: 'Created',
    accessor: 'createdAt',
    width: { fixed: 80 },
    cell: Table.TimeCell,
  },
];

const TAB_OPTIONS: { label: string; value: Tab }[] = [
  { label: 'Check Deposits', value: 'check' },
  { label: 'ACH', value: 'ach' },
];

const isTab = (v: string | null): v is Tab => v === 'check' || v === 'ach';

export const PageTransferBatchesOverview: React.FC = () => {
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  const navigate = useNavigate();
  const achEnabled = useFeatureFlag(FeatureFlag.EnableAchTransferBatches);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: Tab = achEnabled && isTab(tabParam) ? tabParam : 'check';

  const { cursor, onNext, setPageSize, reset, ...pagination } = usePaginationState();

  const scopeKey = `${platformId}|${isSandbox}|${tab}`;
  const [prevScope, setPrevScope] = useState(scopeKey);
  let effectiveCursor: string | undefined = cursor;
  if (prevScope !== scopeKey) {
    setPrevScope(scopeKey);
    reset();
    effectiveCursor = undefined;
  }

  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'check') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const { data: checkData, isLoading: isCheckLoading } = useQuery({
    queryKey: ['transfers', 'batches', platformId, isSandbox, effectiveCursor, pagination.pageSize],
    queryFn: () => TransferBatchRepository.getAll({ limit: pagination.pageSize, startingAfter: effectiveCursor }),
    enabled: tab === 'check',
    gcTime: 0,
    staleTime: 0,
  });

  const { data: achData, isLoading: isAchLoading } = useQuery({
    queryKey: ['ach-batches', platformId, isSandbox, effectiveCursor, pagination.pageSize],
    queryFn: () => AchBatchRepository.getAll({ limit: pagination.pageSize, startingAfter: effectiveCursor }),
    enabled: tab === 'ach',
    gcTime: 0,
    staleTime: 0,
  });

  const paginationProps = { onPageSizeChange: setPageSize, ...pagination };

  return (
    <>
      {achEnabled && (
        <TabBar>
          <SegmentedControl
            options={TAB_OPTIONS}
            active={tab}
            onOptionChange={setTab}
            variant={SegmentedControlVariant.Line}
          />
        </TabBar>
      )}
      <Inner pt={0} px="var(--page-margin)">
        {tab === 'check' && (
          <Table<TransferBatch>
            columns={checkColumns}
            data={checkData?.batches ?? []}
            pagination={{
              hasNext: checkData?.hasMore ?? false,
              onNext: () =>
                checkData ? onNext({ cursor: checkData.batches[checkData.batches.length - 1].id }) : undefined,
              ...paginationProps,
            }}
            isLoading={isCheckLoading}
            rowAction={(row) => (
              <Button
                onClick={(event: React.SyntheticEvent) => {
                  event.stopPropagation();
                  navigate(`${ROUTE.TRANSFERS}/batches/${row.id}`);
                }}
                icon={<Icon.ChevronRightLarge />}
                iconRight
                size="tiny"
                variant="secondary"
              >
                View Batch
              </Button>
            )}
            variant={TableVariant.List}
            onRowClick={(row) => navigate(`${ROUTE.TRANSFERS}/batches/${row.id}`)}
          />
        )}
        {tab === 'ach' && (
          <Table<ACHTransferBatch>
            columns={achColumns}
            data={achData?.batches ?? []}
            pagination={{
              hasNext: achData?.hasMore ?? false,
              onNext: () => (achData ? onNext({ cursor: achData.batches[achData.batches.length - 1].id }) : undefined),
              ...paginationProps,
            }}
            isLoading={isAchLoading}
            rowAction={(row) => (
              <Button
                onClick={(event: React.SyntheticEvent) => {
                  event.stopPropagation();
                  navigate(`${ROUTE.TRANSFERS}/batches/ach/${row.id}`);
                }}
                icon={<Icon.ChevronRightLarge />}
                iconRight
                size="tiny"
                variant="secondary"
              >
                View Batch
              </Button>
            )}
            variant={TableVariant.List}
            onRowClick={(row) => navigate(`${ROUTE.TRANSFERS}/batches/ach/${row.id}`)}
          />
        )}
      </Inner>
    </>
  );
};
